"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { CartIconButton } from "@/components/cart/CartIconButton";
import { useIsAdmin } from "@/components/auth/useIsAdmin";
import { useIsApproved } from "@/components/auth/useIsApproved";

type Lang = "EN" | "ES";
type LocaleCode = "en" | "es";

const NAV_LINKS = [
  { label: "Dashboard", href: "/dashboard" },
  { label: "Catalog", href: "/catalog" },
  { label: "Orders", href: "/dashboard/orders" },
  { label: "Account", href: "/account" },
];

// Step 8.1.2 hotfix: rendered only when useIsAdmin() returns true.
// Same gate as middleware.ts (cognito:groups includes "admin").
const ADMIN_LINK = { label: "Admin", href: "/admin" } as const;

// #421: locale-aware aria-labels for the authed TopBar chrome. A Spanish client
// on a screen reader must hear Spanish navigation. Wording mirrors the public
// MarketingNav (#421-equivalent): Inicio de Magnaflor / Abrir menu / Cerrar
// menu / Idioma; "Panel" matches the ES nav label for Dashboard. EN is unchanged.
const TOPBAR_ARIA = {
  en: {
    home: "Magnaflor home",
    dashboard: "Magnaflor dashboard",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    language: "Language",
  },
  es: {
    home: "Inicio de Magnaflor",
    dashboard: "Panel de Magnaflor",
    openMenu: "Abrir menú",
    closeMenu: "Cerrar menú",
    language: "Idioma",
  },
} as const;

export function TopBar({
  initialLocale = "en",
}: {
  initialLocale?: LocaleCode;
}) {
  const router = useRouter();
  const [lang, setLang] = useState<Lang>(initialLocale === "es" ? "ES" : "EN");
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = useIsAdmin();
  const aria = TOPBAR_ARIA[lang === "ES" ? "es" : "en"];
  // #82 (Jul 14): for an APPROVED signed-in client the logo is a way back to
  // their own home, not out to the marketing site. TopBar also renders for
  // signed-out visitors browsing /catalog + /about, so this keys off approval
  // state, NOT the route. "loading" and "not" both keep the marketing target,
  // so the first client render matches the server HTML.
  const logoHref = useIsApproved() === "approved" ? "/dashboard" : "/";

  function handleLangChange(next: Lang) {
    setLang(next);
    const cookieValue: LocaleCode = next === "ES" ? "es" : "en";
    document.cookie = `mag-locale=${cookieValue}; path=/; max-age=31536000; samesite=lax`;
    router.refresh();
  }

  return (
    <header className="w-full border-b border-brand-border bg-background">
      <div className="mx-auto flex h-16 md:h-20 max-w-7xl items-center justify-between px-4 md:px-6">
        <Link
          href={logoHref}
          aria-label={logoHref === "/dashboard" ? aria.dashboard : aria.home}
          data-testid="topbar-logo"
          onClick={() => setMobileOpen(false)}
          className="inline-flex items-center"
        >
          <Image
            src="/logo-magnaflor.svg"
            alt="Magnaflor Farm Fresh Flowers"
            width={180}
            height={60}
            priority
            className="h-12 w-auto md:h-14"
          />
        </Link>

        <nav className="hidden md:flex items-center rounded-full bg-brand-surface p-1 text-sm font-medium">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="rounded-full px-4 py-2 text-brand-text-muted transition-colors hover:text-brand-text"
            >
              {link.label}
            </Link>
          ))}
          {isAdmin && (
            <Link
              key={ADMIN_LINK.href}
              href={ADMIN_LINK.href}
              data-testid="topbar-admin-link"
              className="inline-flex items-center gap-1 rounded-full px-4 py-2 text-brand-teal transition-colors hover:text-brand-teal-dark"
            >
              <LockIcon />
              {ADMIN_LINK.label}
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-1 md:gap-2">
          <CartIconButton />
          <LangToggle lang={lang} onChange={handleLangChange} label={aria.language} />
          <button
            type="button"
            aria-label={mobileOpen ? aria.closeMenu : aria.openMenu}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
            onClick={() => setMobileOpen((open) => !open)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-brand-text transition-colors hover:bg-brand-surface md:hidden"
          >
            <HamburgerIcon open={mobileOpen} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav
          id="mobile-nav"
          className="border-t border-brand-border bg-background md:hidden"
        >
          <ul className="flex flex-col px-4 py-2">
            {NAV_LINKS.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block py-3 text-base font-medium text-brand-text hover:text-brand-teal"
                >
                  {link.label}
                </Link>
              </li>
            ))}
            {isAdmin && (
              <li key={ADMIN_LINK.href}>
                <Link
                  href={ADMIN_LINK.href}
                  onClick={() => setMobileOpen(false)}
                  data-testid="topbar-admin-link-mobile"
                  className="flex items-center gap-2 py-3 text-base font-medium text-brand-teal hover:text-brand-teal-dark"
                >
                  <LockIcon />
                  {ADMIN_LINK.label}
                </Link>
              </li>
            )}
          </ul>
        </nav>
      )}
    </header>
  );
}

function LangToggle({
  lang,
  onChange,
  label,
}: {
  lang: Lang;
  onChange: (next: Lang) => void;
  label: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="inline-flex items-center rounded-full border border-brand-border bg-background p-0.5 text-xs font-semibold"
    >
      {(["EN", "ES"] as const).map((code) => {
        const active = lang === code;
        return (
          <button
            key={code}
            type="button"
            onClick={() => onChange(code)}
            aria-pressed={active}
            className={
              active
                ? "rounded-full bg-brand-teal px-3 py-1 text-white"
                : "rounded-full px-3 py-1 text-brand-text-muted transition-colors hover:text-brand-text"
            }
          >
            {code}
          </button>
        );
      })}
    </div>
  );
}

function LockIcon() {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="4" y="9" width="12" height="9" rx="1.5" />
      <path d="M7 9V6a3 3 0 0 1 6 0v3" />
    </svg>
  );
}

function HamburgerIcon({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      {open ? (
        <>
          <line x1="5" y1="5" x2="15" y2="15" />
          <line x1="15" y1="5" x2="5" y2="15" />
        </>
      ) : (
        <>
          <line x1="3" y1="6" x2="17" y2="6" />
          <line x1="3" y1="10" x2="17" y2="10" />
          <line x1="3" y1="14" x2="17" y2="14" />
        </>
      )}
    </svg>
  );
}
